import { useCloseDialog } from "hooks/useCloseDialog";
import { ModalAdapterProps } from "constants/navigation";
import { useDispatch } from "react-redux";
import { HASH_MODAL_CLOSED } from "constants/actionTypes";
import { useCallback } from "react";

// Named type alias for the tuple:
export type ModalAdapterReturn<TParams = unknown> = [
  params: TParams,
  returnTo: string,
  closeDialog: ReturnType<typeof useCloseDialog>,
];

/**
 * Provides utility functions for managing hash modals.
 */
export const useModalAdapter = <TParams = unknown>(
  modalType: string,
  modalProps: ModalAdapterProps,
): ModalAdapterReturn<TParams> => {
  const dispatch = useDispatch();

  const { hash } = modalProps;
  const params = JSON.parse(hash[modalType] || null);

  const { returnTo } = hash;
  const decodedReturnTo = returnTo ? atob(returnTo) : "";

  const closeDialog = useCloseDialog(decodedReturnTo);

  const closeDialogWithDispatch = useCallback(() => {
    // on close dispatch HASH_MODAL_CLOSED action.
    // So that we can run logic after a hash modal is closed in sagas.
    // it enables openHashModalAndWait on any modal that uses useModalAdapter.
    closeDialog();
    dispatch({ type: HASH_MODAL_CLOSED, modalType });
  }, [closeDialog, dispatch, modalType]);

  return [params, decodedReturnTo, closeDialogWithDispatch];
};
